import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Honeybadger from '@honeybadger-io/js';

import { isDevelopment } from '/src/utils';
import { CE2BlockedModal } from '/src/features/addons/common/modals/CE2BlockedModal';
import { useModal } from '/src/hooks';
import { FEATURES } from '/src/features/_global/constants';

export function useAddonScript(readyCallback, type) {
  const modal = useModal();
  const navigate = useNavigate();

  useEffect(() => {
    let retryTimeout;
    let cancelled = false;

    if (!window.CE2) {
      //Honeybadger.notify('window.CE2 is not available when trying to load addon script');
      modal.show(
        <CE2BlockedModal
          onConfirm={() => {
            navigate(type === 'cta' ? `/addons?type=${FEATURES.CTAS}` : `/addons?type=${FEATURES.SURVEYS}`);
          }}
        />,
      );
      return;
    }

    const MAX_VERSION_RETRIES = 10;
    const VERSION_RETRY_DELAY_MS = 300;

    function loadScripts() {
      if (type === 'survey') {
        if (window.CE2.loadAddon) {
          window.CE2.loadAddon?.(type);
        } else {
          window.CE2.loadSurveyScripts?.();
        }
        if (window.CE2.Survey) {
          readyCallback?.();
        } else {
          window.addEventListener('surveyLoaded', () => {
            readyCallback?.();
          });
        }
      } else {
        window.CE2.loadAddon?.(type);
        if (window.CE2.CTA) {
          readyCallback?.();
        } else {
          window.addEventListener('ctaLoaded', () => {
            readyCallback?.();
          });
        }
      }
    }

    function getScriptSrc() {
      if (isDevelopment()) {
        return typeof window.ADDON_SCRIPT_URL === 'function' ? window.ADDON_SCRIPT_URL() : window.ADDON_SCRIPT_URL;
      }

      const addonVersion = window.CE2?.ADDON_SCRIPT_VERSION;
      const surveyVersion = window.CE2?.SURVEY_VERSION;

      if (addonVersion) {
        return `https://script.crazyeggcdn.com/scripts/addons/${addonVersion}/index.js`;
      }

      if (surveyVersion) {
        return `https://script.crazyeggcdn.com/scripts/survey/${surveyVersion}/index.js`;
      }

      return null;
    }

    function loadScriptWithRetry(attempt = 0) {
      if (cancelled) {
        return;
      }

      const src = getScriptSrc();

      if (!src) {
        if (attempt < MAX_VERSION_RETRIES) {
          console.warn(
            `[useAddonScript] Missing CE2 script version for type="${type}". Retrying (${attempt + 1}/${MAX_VERSION_RETRIES})`,
          );
          retryTimeout = window.setTimeout(() => {
            loadScriptWithRetry(attempt + 1);
          }, VERSION_RETRY_DELAY_MS);
        } else {
          console.error(
            `[useAddonScript] Missing CE2 script version for type="${type}" after ${MAX_VERSION_RETRIES} retries.`,
          );
        }
        return;
      }

      if ((window.CE2.Survey && type === 'survey') || (window.CE2.CTA && type === 'cta')) {
        readyCallback?.();
        return;
      }

      const isScriptPresent = document.querySelector(`script[src="${src}"]`);

      if (isScriptPresent) {
        loadScripts();
      } else {
        const scriptTag = document.createElement('script');
        scriptTag.onload = () => {
          loadScripts();
        };
        scriptTag.onerror = () => {
          Honeybadger.notify(`Failed to load addon script: ${src}`);
        };
        scriptTag.src = src;
        document.body.appendChild(scriptTag);
      }
    }

    loadScriptWithRetry();

    return () => {
      cancelled = true;
      if (retryTimeout) {
        window.clearTimeout(retryTimeout);
      }
    };
  }, [readyCallback, type, modal, navigate]);
}
